// Import directly from the product-keys source file (not the re-exporting `../products`
// barrel) to avoid a circular import that leaves ProductKeys3PValues undefined when this
// module is loaded via certain quick-find code paths.
import type { ProductKeys3P } from '../product-keys';
import { ProductKeys3P as ProductKeys3PValues } from '../product-keys';

import type { FilterCondition } from './filter-exclusion-rules';
import { TypeFilterValueKey as TypeFilterValueKeyValues } from './universal-type';

import type { FilterId } from './index';

/** At least one element — prevents vacuous rules with empty arrays. */
type NonEmpty<T> = [T, ...T[]];

/**
 * Defines a rule for conditionally showing filters that are hidden by default.
 *
 * The inverse of FilterExclusionRule: filters referenced in inclusion rules
 * are HIDDEN unless at least one matching rule has ALL of its conditions met.
 *
 * A rule activates when ALL of the following are true:
 * - If `selectedProduct` is specified, it matches the currently active product
 * - ALL `selectedFilters` conditions match
 *
 * When at least one rule activates for a given filter, that filter is shown.
 * If NO inclusion rules activate AND the user has made a relevant choice in
 * one of the condition filters, the managed filter is hidden. If no condition
 * filter has a selection yet (e.g. "All" mode), the managed filter follows
 * its normal visibility — it is NOT hidden prematurely.
 *
 * When hidden, filters with active selections are still excluded: the URL/GraphQL/
 * UI/badge consumers do not see the selection, and `setSearchStoreState` clears
 * the underlying store state so it stays in sync with what is visible/applied.
 *
 * @example
 * // Show "salesforceStage" only when Salesforce is selected AND
 * // the universalType filter has "deal" selected (or no selection / "All"):
 * {
 *   selectedProduct: ProductKeys3PValues.Salesforce,
 *   selectedFilters: [['universalTypeFilter', TypeFilterValueKeyValues.Deal]],
 *   shownFilters: ['salesforceStage'],
 * }
 */
export type FilterInclusionRule = {
	/** Optional: The 3P product that must be selected. */
	selectedProduct?: ProductKeys3P;
	/**
	 * Array of [FilterId, value] conditions. ALL must match for the rule to activate.
	 * Values are type-checked against the FilterValueRegistry.
	 */
	selectedFilters: NonEmpty<FilterCondition>;
	/** Filter IDs to show when this rule activates. Must have at least one entry. */
	shownFilters: NonEmpty<FilterId>;
};

/**
 * Centralized list of filter inclusion rules.
 *
 * Filters referenced in these rules are hidden by default and only shown
 * when at least one matching rule has ALL of its conditions met — OR when
 * no condition filter has an active selection yet ("All" mode).
 *
 * Multiple rules can target the same filter — if ANY rule matches, the filter
 * is shown (OR logic across rules, AND logic within a rule).
 *
 * To add a new rule, append an entry to this array.
 */
export const FILTER_INCLUSION_RULES: FilterInclusionRule[] = [
	{
		selectedProduct: ProductKeys3PValues.Salesforce,
		selectedFilters: [['universalTypeFilter', TypeFilterValueKeyValues.Deal]],
		shownFilters: ['salesforceStage'],
	},
	{
		selectedProduct: ProductKeys3PValues.Salesforce,
		selectedFilters: [['universalTypeFilter', TypeFilterValueKeyValues.Case]],
		shownFilters: ['caseStatus'],
	},
	{
		selectedProduct: ProductKeys3PValues.Salesforce,
		selectedFilters: [['universalTypeFilter', TypeFilterValueKeyValues.Campaign]],
		shownFilters: ['campaignStatus'],
	},
];
